/**
 * Whether a `rounded-…` value names one of this workspace's radii.
 *
 * The same shape of problem as `typography-scale.ts`, one utility over. The Tailwind preset replaces
 * the stock `borderRadius` scale with literal pixels — `rounded-8` is 8px — and tailwind-merge only
 * knows the t-shirt sizes, so it recognises neither `rounded-8` nor `rounded-12` as belonging to the
 * radius group and keeps both. Which one then applies is decided by Tailwind's emission order, where
 * the values sort as *strings*: `rounded-8` is emitted after `rounded-12`, so a caller asking for the
 * wider radius silently got the narrower one — no error, and nothing for lint to see.
 *
 * Matched by shape rather than by a list: every key in the preset's `borderRadius` block is a pixel
 * value bar `switch`, `full` and `none`, and the last two tailwind-merge already knows. A radius added
 * to the preset is therefore understood without this file being edited.
 *
 * Dependency-free on purpose: the test step runs without `npm install`, so the part worth testing
 * lives here rather than in `cn.ts`, which imports tailwind-merge.
 *
 * @see packages/ui/src/tailwind/themes.ts — the `borderRadius` block this mirrors
 */
export const RADIUS_SCALE_PATTERN = /^(?:\d+|switch)$/;

export function isRadiusScaleClass(value: string): boolean {
    return RADIUS_SCALE_PATTERN.test(value);
}

/**
 * Every tailwind-merge class group that takes a radius: the all-corners one plus the logical, side
 * and single-corner variants. Named here rather than inline in `cn.ts` so the sided groups cannot be
 * half-covered — `rounded-t-8 rounded-t-12` has exactly the same failure as the base group.
 */
export const RADIUS_CLASS_GROUPS = [
    'rounded',
    'rounded-s',
    'rounded-e',
    'rounded-t',
    'rounded-r',
    'rounded-b',
    'rounded-l',
    'rounded-ss',
    'rounded-se',
    'rounded-ee',
    'rounded-es',
    'rounded-tl',
    'rounded-tr',
    'rounded-br',
    'rounded-bl'
] as const;
